/**
 * Vuetify Plugin Configuration
 * Configured for European Accessibility Act (EAA) Compliance
 *
 * EAA Requirements:
 * - WCAG 2.1 Level AA compliance
 * - Keyboard navigation
 * - Screen reader support
 * - High contrast support
 * - Focus indicators
 * - Proper color contrast ratios
 */

import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'

// Custom theme using existing CSS variables from variables.scss
// --primary-color: #001e46
// --secondary-color: #00aeef
// --success-color: #78b51a
// --error-color: #ff0000
const customTheme = {
  dark: false,
  colors: {
    // Primary colors from existing CSS variables
    primary: '#001e46', // --primary-color (dark navy blue)
    secondary: '#00aeef', // --secondary-color (cyan blue)

    // Semantic colors using existing CSS variables
    success: '#78b51a', // --success-color (lime green)
    error: '#ff0000', // --error-color (red)
    warning: '#F77F00', // Orange - WCAG AA compliant
    info: '#0077B6', // Blue - WCAG AA compliant

    // Background and surface colors
    background: '#fafafa', // --body-background
    surface: '#FFFFFF', // --color-white
    'surface-variant': '#F5F5F5',

    // Text colors with proper contrast
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    'on-background': '#001e46', // --primary-color
    'on-surface': '#001e46', // --primary-color
    'on-success': '#FFFFFF',
    'on-error': '#FFFFFF',
    'on-warning': '#000000',
    'on-info': '#FFFFFF',
  },
}

// High contrast theme for accessibility
const highContrastTheme = {
  dark: false,
  colors: {
    primary: '#000000',
    secondary: '#CC0000',
    success: '#006600',
    error: '#990000',
    warning: '#CC6600',
    info: '#0066CC',
    background: '#FFFFFF',
    surface: '#FFFFFF',
    'surface-variant': '#F0F0F0',
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    'on-background': '#000000',
    'on-surface': '#000000',
    'on-success': '#FFFFFF',
    'on-error': '#FFFFFF',
    'on-warning': '#FFFFFF',
    'on-info': '#FFFFFF',
  },
}

const vuetify = createVuetify({
  components,
  directives,

  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },

  theme: {
    defaultTheme: 'customTheme',
    themes: {
      customTheme,
      highContrastTheme,
    },
    // Disable dark mode - always use light theme
    variations: {
      colors: [],
      lighten: 0,
      darken: 0,
    },
  },

  // Default component configurations for accessibility
  defaults: {
    VBtn: {
      // Minimum touch target size: 44x44px (WCAG 2.1 Level AAA)
      minWidth: '44px',
      minHeight: '44px',
      // Ensure buttons are keyboard accessible
      tabindex: 0,
    },
    VTextField: {
      variant: 'outlined',
      color: 'primary',
      // Clear error states for screen readers
      persistentHint: true,
    },
    VSelect: {
      variant: 'outlined',
      color: 'primary',
      persistentHint: true,
    },
    VTextarea: {
      variant: 'outlined',
      color: 'primary',
      persistentHint: true,
    },
    VCheckbox: {
      color: 'primary',
    },
    VRadio: {
      color: 'primary',
    },
    VSwitch: {
      color: 'primary',
      // Minimum touch target
      inset: false,
    },
  },

  // Display options for accessibility
  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
})

export default vuetify

/**
 * Accessibility Helper Functions
 */

// Check if user prefers reduced motion
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Check if user prefers high contrast
export const prefersHighContrast = () => {
  return window.matchMedia('(prefers-contrast: high)').matches
}

// Apply high contrast theme if user preference detected
export const applyAccessibilityPreferences = (vuetifyInstance) => {
  if (prefersHighContrast()) {
    vuetifyInstance.theme.global.name.value = 'highContrastTheme'
  }

  // Listen for preference changes
  window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
    vuetifyInstance.theme.global.name.value = e.matches ? 'highContrastTheme' : 'customTheme'
  })
}
